import { useState, useEffect, useCallback, useMemo } from 'react'
import useApiService from 'services/api'
import { ContactCompanySearchResult, DoorSearchResult } from 'models/companies'
import { searchVariants, VariantSearchResult } from 'services/search_api'
import { transformFirstBiteResults } from '../utils/searchUtils'
import { Filters, MIN_SEARCH_CHARS, UnifiedSearchTab } from '../constants'

// Helper to extract value from settled promise or return default empty response
function getSettledValue<T>(result: PromiseSettledResult<T>): T {
  if (result.status === 'fulfilled') {
    return result.value
  }
  return { results: [], total: 0 } as T
}

export type SearchResultUnion =
  | VariantSearchResult
  | ContactCompanySearchResult
  | DoorSearchResult

export type SearchType = 'address' | 'domain' | 'name' | 'sku'

interface SearchResults {
  firstBiteResults: ContactCompanySearchResult[]
  unmatchedResults: ContactCompanySearchResult[]
  firstBiteDoorResults: any[]
  unmatchedDoorResults: any[]
  variantResults: VariantSearchResult[]
  firstBiteCount: number
  unmatchedCount: number
  firstBiteDoorCount: number
  unmatchedDoorCount: number
  variantCount: number
  firstBiteTotalRows: number
  unmatchedTotalRows: number
  firstBiteDoorTotalRows: number
  unmatchedDoorTotalRows: number
  variantTotalRows: number
  isLoading: boolean
  currentResults: SearchResultUnion[]
  currentTotalRows: number
  searchType: SearchType | null
  refetchVariants: () => void
  clearResults: () => void
}

interface UseUnifiedSearchAPIProps {
  searchTerm: string
  filters: Filters
  sortBy: string
  firstBitePage: number
  unmatchedPage: number
  firstBiteDoorPage: number
  unmatchedDoorPage: number
  variantPage: number
  selectedTab: UnifiedSearchTab
  pageSize: number
}

export function useUnifiedSearchAPI({
  searchTerm,
  filters,
  sortBy,
  firstBitePage,
  unmatchedPage,
  firstBiteDoorPage,
  unmatchedDoorPage,
  variantPage,
  selectedTab,
  pageSize,
}: UseUnifiedSearchAPIProps): SearchResults {
  const api = useApiService()

  // State for search results
  const [isLoading, setIsLoading] = useState(false)
  const [firstBiteResults, setFirstBiteResults] = useState<
    ContactCompanySearchResult[]
  >([])
  const [unmatchedResults, setUnmatchedResults] = useState<
    ContactCompanySearchResult[]
  >([])
  const [firstBiteDoorResults, setFirstBiteDoorResults] = useState<
    DoorSearchResult[]
  >([])
  const [unmatchedDoorResults, setUnmatchedDoorResults] = useState<
    DoorSearchResult[]
  >([])
  const [variantResults, setVariantResults] = useState<VariantSearchResult[]>(
    []
  )
  const [firstBiteCount, setFirstBiteCount] = useState(0)
  const [unmatchedCount, setUnmatchedCount] = useState(0)
  const [firstBiteDoorCount, setFirstBiteDoorCount] = useState(0)
  const [unmatchedDoorCount, setUnmatchedDoorCount] = useState(0)
  const [variantCount, setVariantCount] = useState(0)
  const [firstBiteTotalRows, setFirstBiteTotalRows] = useState(0)
  const [unmatchedTotalRows, setUnmatchedTotalRows] = useState(0)
  const [firstBiteDoorTotalRows, setFirstBiteDoorTotalRows] = useState(0)
  const [unmatchedDoorTotalRows, setUnmatchedDoorTotalRows] = useState(0)
  const [variantTotalRows, setVariantTotalRows] = useState(0)
  const [searchType, setSearchType] = useState<SearchType | null>(null)

  // Build search options for both tabs
  const firstBiteSearchOptions = useMemo(() => {
    return {
      category_filter: filters.category === 'all' ? 'all' : filters.category,
      type_filter: filters.type === 'all' ? 'all' : filters.type,
      sort_by: sortBy,
      limit: pageSize,
      offset: (firstBitePage - 1) * pageSize,
    }
  }, [filters.category, filters.type, sortBy, pageSize, firstBitePage])

  const unmatchedSearchOptions = useMemo(() => {
    return {
      category_filter: filters.category === 'all' ? 'all' : filters.category,
      type_filter: filters.type === 'all' ? 'all' : filters.type,
      sort_by: sortBy,
      limit: pageSize,
      offset: (unmatchedPage - 1) * pageSize,
    }
  }, [filters.category, filters.type, sortBy, pageSize, unmatchedPage])

  const firstBiteDoorSearchOptions = useMemo(() => {
    return {
      category_filter: filters.category === 'all' ? 'all' : filters.category,
      type_filter: filters.type === 'all' ? 'all' : filters.type,
      sort_by: sortBy,
      limit: pageSize,
      offset: (firstBiteDoorPage - 1) * pageSize,
    }
  }, [filters.category, filters.type, sortBy, pageSize, firstBiteDoorPage])

  const unmatchedDoorSearchOptions = useMemo(() => {
    return {
      category_filter: filters.category === 'all' ? 'all' : filters.category,
      type_filter: filters.type === 'all' ? 'all' : filters.type,
      sort_by: sortBy,
      limit: pageSize,
      offset: (unmatchedDoorPage - 1) * pageSize,
    }
  }, [filters.category, filters.type, sortBy, pageSize, unmatchedDoorPage])

  const variantSearchOptions = useMemo(() => {
    const options: { limit: number; offset: number; product_id?: number } = {
      limit: pageSize,
      offset: (variantPage - 1) * pageSize,
    }
    // Add product filter if selected
    if (filters.product && filters.product !== 'all') {
      options.product_id = parseInt(filters.product, 10)
    }
    return options
  }, [pageSize, variantPage, filters.product])

  // Refetch variants only (for use after edit/delete)
  const refetchVariants = useCallback(async () => {
    if (!searchTerm.trim() || searchTerm.length < MIN_SEARCH_CHARS) return

    try {
      const variantData = await searchVariants(searchTerm, variantSearchOptions)
      const variantResultsData = variantData.results || []
      const variantTotal = variantData.total || 0

      setVariantResults(variantResultsData)
      setVariantCount(variantTotal)
      setVariantTotalRows(variantTotal)
    } catch (error) {
      console.error('Error refetching variants:', error)
    }
  }, [searchTerm, variantSearchOptions])

  // Single search function that handles all tabs
  const performSearch = useCallback(
    async (
      searchValue: string,
      fbOptions: any,
      umOptions: any,
      fbDoorOptions: any,
      umDoorOptions: any,
      varOptions: { limit?: number; offset?: number }
    ) => {
      if (!searchValue.trim()) {
        setFirstBiteResults([])
        setUnmatchedResults([])
        setFirstBiteDoorResults([])
        setUnmatchedDoorResults([])
        setVariantResults([])
        setFirstBiteCount(0)
        setUnmatchedCount(0)
        setFirstBiteDoorCount(0)
        setUnmatchedDoorCount(0)
        setVariantCount(0)
        setFirstBiteTotalRows(0)
        setUnmatchedTotalRows(0)
        setFirstBiteDoorTotalRows(0)
        setUnmatchedDoorTotalRows(0)
        setVariantTotalRows(0)
        setSearchType(null)
        return
      }

      try {
        setIsLoading(true)
        // Clear the search type when starting a new search
        setSearchType(null)

        const results = await Promise.allSettled([
          api.searchFirstBiteCompanies(searchValue, fbOptions),
          api.searchUnmatchedCompanies(searchValue, umOptions),
          api.searchFirstBiteDoors(searchValue, fbDoorOptions),
          api.searchUnmatchedDoors(searchValue, umDoorOptions),
          searchVariants(searchValue, varOptions),
        ])

        const firstBiteData = getSettledValue(results[0])
        const unmatchedData = getSettledValue(results[1])
        const firstBiteDoorData = getSettledValue(results[2])
        const unmatchedDoorData = getSettledValue(results[3])
        const variantData = getSettledValue(results[4])

        // Handle response format
        const firstBiteResults = firstBiteData.results || []
        const unmatchedResults = unmatchedData.results || []
        const firstBiteDoorResults = firstBiteDoorData.results || []
        const unmatchedDoorResults = unmatchedDoorData.results || []
        const variantResultsData = variantData.results || []
        const firstBiteTotal = firstBiteData.total || 0
        const unmatchedTotal = unmatchedData.total || 0
        const firstBiteDoorTotal = firstBiteDoorData.total || 0
        const unmatchedDoorTotal = unmatchedDoorData.total || 0
        const variantTotal = variantData.total || 0

        // Get search type from any of the responses (they should all be the same)
        const detectedSearchType =
          firstBiteData.search_type ||
          unmatchedData.search_type ||
          firstBiteDoorData.search_type ||
          unmatchedDoorData.search_type ||
          'name'

        // Transform First Bite results
        const transformedFirstBite = transformFirstBiteResults(firstBiteResults)

        setFirstBiteResults(transformedFirstBite)
        setUnmatchedResults(unmatchedResults)
        setFirstBiteDoorResults(firstBiteDoorResults)
        setUnmatchedDoorResults(unmatchedDoorResults)
        setVariantResults(variantResultsData)
        setFirstBiteCount(firstBiteTotal)
        setUnmatchedCount(unmatchedTotal)
        setFirstBiteDoorCount(firstBiteDoorTotal)
        setUnmatchedDoorCount(unmatchedDoorTotal)
        setVariantCount(variantTotal)
        setFirstBiteTotalRows(firstBiteTotal)
        setUnmatchedTotalRows(unmatchedTotal)
        setFirstBiteDoorTotalRows(firstBiteDoorTotal)
        setUnmatchedDoorTotalRows(unmatchedDoorTotal)
        setVariantTotalRows(variantTotal)
        setSearchType(detectedSearchType as SearchType)
      } catch (error) {
        // Reset on error
        setFirstBiteResults([])
        setUnmatchedResults([])
        setFirstBiteDoorResults([])
        setUnmatchedDoorResults([])
        setVariantResults([])
        setFirstBiteCount(0)
        setUnmatchedCount(0)
        setFirstBiteDoorCount(0)
        setUnmatchedDoorCount(0)
        setVariantCount(0)
        setFirstBiteTotalRows(0)
        setUnmatchedTotalRows(0)
        setFirstBiteDoorTotalRows(0)
        setUnmatchedDoorTotalRows(0)
        setVariantTotalRows(0)
        setSearchType(null)
      } finally {
        setIsLoading(false)
      }
    },
    [api]
  )

  const clearResults = useCallback(() => {
    setFirstBiteResults([])
    setUnmatchedResults([])
    setFirstBiteDoorResults([])
    setUnmatchedDoorResults([])
    setVariantResults([])
    setFirstBiteCount(0)
    setUnmatchedCount(0)
    setFirstBiteDoorCount(0)
    setUnmatchedDoorCount(0)
    setVariantCount(0)
    setFirstBiteTotalRows(0)
    setUnmatchedTotalRows(0)
    setFirstBiteDoorTotalRows(0)
    setUnmatchedDoorTotalRows(0)
    setVariantTotalRows(0)
    setSearchType(null)
  }, [])

  // Trigger search when search term or options change
  useEffect(() => {
    if (searchTerm.length >= MIN_SEARCH_CHARS) {
      performSearch(
        searchTerm,
        firstBiteSearchOptions,
        unmatchedSearchOptions,
        firstBiteDoorSearchOptions,
        unmatchedDoorSearchOptions,
        variantSearchOptions
      )
    } else {
      setFirstBiteResults([])
      setUnmatchedResults([])
      setFirstBiteDoorResults([])
      setUnmatchedDoorResults([])
      setVariantResults([])
      setFirstBiteCount(0)
      setUnmatchedCount(0)
      setFirstBiteDoorCount(0)
      setUnmatchedDoorCount(0)
      setVariantCount(0)
      setFirstBiteTotalRows(0)
      setUnmatchedTotalRows(0)
      setFirstBiteDoorTotalRows(0)
      setUnmatchedDoorTotalRows(0)
      setVariantTotalRows(0)
    }
  }, [
    searchTerm,
    firstBiteSearchOptions,
    unmatchedSearchOptions,
    firstBiteDoorSearchOptions,
    unmatchedDoorSearchOptions,
    variantSearchOptions,
    performSearch,
  ])

  // Handle client-side sorting for non-relevance sorts
  useEffect(() => {
    if (sortBy === 'relevance') return

    const sortResults = <T extends { name: string; size?: number }>(
      results: T[]
    ): T[] => {
      return [...results].sort((a, b) => {
        switch (sortBy) {
          case 'alphabetical_asc':
          case 'alphabetical':
            return a.name.localeCompare(b.name)
          case 'alphabetical_desc':
            return b.name.localeCompare(a.name)
          case 'size_desc':
          case 'size':
            return (b.size || 0) - (a.size || 0)
          case 'size_asc':
            return (a.size || 0) - (b.size || 0)
          default:
            return 0
        }
      })
    }

    if (firstBiteResults.length > 0) {
      setFirstBiteResults(sortResults(firstBiteResults))
    }
    if (unmatchedResults.length > 0) {
      setUnmatchedResults(sortResults(unmatchedResults))
    }
    if (firstBiteDoorResults.length > 0) {
      setFirstBiteDoorResults(sortResults(firstBiteDoorResults))
    }
    if (unmatchedDoorResults.length > 0) {
      setUnmatchedDoorResults(sortResults(unmatchedDoorResults))
    }
  }, [
    sortBy,
    firstBiteResults.length,
    unmatchedResults.length,
    firstBiteDoorResults.length,
    unmatchedDoorResults.length,
  ])

  // Get current results based on selected tab
  const currentResults =
    selectedTab === 'firstbite'
      ? firstBiteResults
      : selectedTab === 'firstbitedoors'
        ? firstBiteDoorResults
        : selectedTab === 'unmatcheddoors'
          ? unmatchedDoorResults
          : selectedTab === 'variants'
            ? variantResults
            : unmatchedResults
  const currentTotalRows =
    selectedTab === 'firstbite'
      ? firstBiteTotalRows
      : selectedTab === 'firstbitedoors'
        ? firstBiteDoorTotalRows
        : selectedTab === 'unmatcheddoors'
          ? unmatchedDoorTotalRows
          : selectedTab === 'variants'
            ? variantTotalRows
            : unmatchedTotalRows

  return {
    firstBiteResults,
    unmatchedResults,
    firstBiteDoorResults,
    unmatchedDoorResults,
    variantResults,
    firstBiteCount,
    unmatchedCount,
    firstBiteDoorCount,
    unmatchedDoorCount,
    variantCount,
    firstBiteTotalRows,
    unmatchedTotalRows,
    firstBiteDoorTotalRows,
    unmatchedDoorTotalRows,
    variantTotalRows,
    isLoading,
    currentResults,
    currentTotalRows,
    searchType,
    refetchVariants,
    clearResults,
  }
}
